import * as React from 'react';
import NameTextInput from './fields/NameTextInput';
import EmailTextInput from './fields/EmailTextInput';
import PhoneTextInput from './fields/PhoneTextInput';
import { MultiLineTextInput } from './fields/MultiLineTextInput';
import Button from './Button';
import { useUser } from '../contexts/UserContext';
import useCreatePropertyInquiry from '@th/frontend-shared/src/hooks/mutations/useCreatePropertyInquiry';
import nameValidator from '@th/shared/src/validators/nameValidator';
import emailValidator from '@th/shared/src/validators/emailValidator';
import phoneValidator from '@th/shared/src/validators/phoneValidator';
import { useState } from 'react';
import { SimpleListing } from '@th/shared/src/types/SimpleListing';
import { Agent } from '@th/shared/src/types/Agent';
import logEvent from '../helpers/mutations/logEvent';
import {
  AnalyticsEventAction,
  AnalyticsEventType,
} from '@th/shared/src/types/AnalyticsEvent';
import {
  REQUEST_INFO_LABEL,
  REQUEST_INFO_SUCCESS_LABEL,
  SCHEDULE_VIEWING_LABEL,
  SCHEDULE_VIEWING_SUCCESS_LABEL,
} from '@th/shared/src/constants/propertyInquiry';
import createAdminNotification from '@th/frontend-shared/src/helpers/mutations/createAdminNotification';

type Props = {
  listing: SimpleListing;
  agent?: Agent;
};

export default function PropertyInquiryForm({ listing, agent }: Props) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');

  const [message, setMessage] = useState(
    `I'm interested in ${
      listing.isActive()
        ? 'booking an appointment to view'
        : 'getting more information on'
    } ${listing.address.formattedAddressShortOneLiner()}`,
  );
  const [error, setError] = useState<string>();
  const { user, isLoggedIn } = useUser();
  const {
    mutateAsync,
    isLoading,
    isSuccess: isCreatePropertyInquiryMutationSuccess,
  } = useCreatePropertyInquiry();
  const [showInvalid, setShowInvalid] = useState(false);
  const [
    isAnonymousPropertyInquiryLogSuccess,
    setIsAnonymousPropertyInquiryLogSuccess,
  ] = useState(false);

  const isNameValid = nameValidator.required().isValidSync(name);
  const isEmailValid = emailValidator.required().isValidSync(email);
  const isPhoneValid = phoneValidator.required().isValidSync(phone);

  function attemptSubmit() {
    setError(null);
    setShowInvalid(true);

    const isFormValid = isNameValid && isEmailValid && isPhoneValid;

    if (isFormValid) {
      if (isLoggedIn) {
        mutateAsync({
          userId: user.id,
          userName: name,
          userPhoneNumber: phone,
          userEmail: email,
          userMessage: message,
          listingId: listing.id,
          agentId: agent?.agentId,
        });
      } else {
        // TODO https://linear.app/tophouse/issue/TOP-822/handle-the-anonymous-property-inquiry-better
        // This is a shortcut for now. This should probably:
        // 1. Send the user a login email
        // 2. Replace the button with some text like "please click on the link in your email to confirm the booking"
        // 3. Create a proper inquiry after they login
        // 4. We should still log the inquiry somehow, so we still have a record, even if they don't complete the login
        // Note that this is a problem when browsing on an agent's website (eg. melody.tophouse.com), since we don't notify them of the inquiry!
        createAdminNotification({
          title: `Property Inquiry by non-signed in person - THIS IS NOT RECORDED IN DB!`,
          body: `Name: ${name}, Email: ${email}, Phone: ${phone}, Message: ${message}, Listing: ${listing.id}, Agent: ${agent?.agentId}`,
        });
        setIsAnonymousPropertyInquiryLogSuccess(true);
      }
    }
  }

  const isCreatePropertyInquirySuccess =
    isCreatePropertyInquiryMutationSuccess ||
    isAnonymousPropertyInquiryLogSuccess;

  return (
    <div className="flex flex-col gap-y-2">
      <NameTextInput
        name={name}
        setName={setName}
        showValid
        showInvalid={showInvalid}
      />
      <EmailTextInput
        email={email}
        setEmail={setEmail}
        showValid
        showInvalid={showInvalid}
      />
      <PhoneTextInput
        phone={phone}
        setPhone={setPhone}
        showValid
        showInvalid={showInvalid}
      />
      <MultiLineTextInput
        id={'request-text-input-body'}
        value={message}
        onChange={e => setMessage(e.target.value)}
      />
      <p className="text-xs text-secondaryText">
        By submitting this form, I understand TopHouse will share my information
        with registered real estate professionals
      </p>
      {error && <p className="text-error">{error}</p>}
      {isCreatePropertyInquirySuccess ? (
        <Button
          title={
            listing.isActive()
              ? SCHEDULE_VIEWING_SUCCESS_LABEL
              : REQUEST_INFO_SUCCESS_LABEL
          }
          onClick={() => {}}
          disabled={true}
          buttonClassName="bg-success hover:bg-success active:bg-success cursor-auto opacity-100 text-text hover:text-text active:text-text"
        />
      ) : (
        <Button
          title={
            listing.isActive() ? SCHEDULE_VIEWING_LABEL : REQUEST_INFO_LABEL
          }
          isLoading={isLoading}
          buttonClassName="w-full"
          onClick={() => {
            logEvent({
              action: AnalyticsEventAction.submitted_property_inquiry,
              type: AnalyticsEventType.property_inquiry_submission,
            });
            attemptSubmit();
          }}
        />
      )}
    </div>
  );
}
